import {
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  Routes,
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { UniversalLoadingState, CookieBanner, GoogleAdsConsentBridge } from '@afrilink/ui';
import { createPortalQueryClient } from '@/shared/lib/queryClient';
import { siteConfig } from '@afrilink/config';
import { SignInPage, SignUpPage } from '@/domains/auth';
import { ClientRouter } from '@/apps/client';
import { AdminRouter } from '@/apps/admin';
import { SalesRouter } from '@/apps/sales';
import PayInvoicePage from '@/apps/PayInvoicePage';
import { AuthProvider } from '@/shared/contexts/AuthContext';
import { StaffRoute } from '@/shared/components/routing/StaffRoute';
import { SalesRoute } from '@/shared/components/routing/SalesRoute';
import { ProtectedRoute } from '@/shared/components/routing/ProtectedRoute';
import { HomeRedirect } from '@/shared/components/routing/HomeRedirect';
import { PartnerTokenCapture } from '@/shared/components/routing/PartnerTokenCapture';

/**
 * Top-level App.
 *
 * Uses the React Router 7 data-router (`createBrowserRouter`) with a
 * `HydrateFallback` on the root route so RR7 doesn't emit
 * "No HydrateFallback element provided to render during initial hydration"
 * on every page load. We render purely client-side (createRoot, no SSR),
 * but RR7 still wants the fallback registered.
 *
 * The route tree itself is kept declarative via <Routes> inside <AppRoutes>
 * — same shape as before, no behavioural change.
 */

function PortalHydrateFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <UniversalLoadingState variant="spinner" caption="Loading…" />
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/sign-in" element={<SignInPage />} />
      <Route path="/login" element={<Navigate to="/sign-in" replace />} />
      <Route path="/signup" element={<SignUpPage />} />
      <Route path="/sign-up" element={<Navigate to="/signup" replace />} />

      {/* Public PayFast checkout entry — token-authenticated, no portal sign-in
          required. Linked from the "Pay R… by card" button in invoice emails.
          See portal/src/apps/PayInvoicePage.tsx and the
          payfast-public-initiate Edge Function. */}
      <Route path="/pay/:id" element={<PayInvoicePage />} />

      <Route element={<ProtectedRoute />}>
        <Route path="/app/*" element={<ClientRouter />} />
      </Route>

      <Route element={<StaffRoute />}>
        <Route path="/admin/*" element={<AdminRouter />} />
      </Route>

      <Route element={<SalesRoute />}>
        <Route path="/sales/*" element={<SalesRouter />} />
      </Route>

      <Route path="/" element={<HomeRedirect />} />
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  );
}

// Module scope, not inside a component — a client recreated on render would
// throw the whole cache away on every re-render.
const queryClient = createPortalQueryClient();

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      element={
        // QueryClientProvider sits ABOVE AuthProvider so a sign-out can clear
        // the cache, and so every route (including the public /pay page) shares
        // one cache — two components asking for the same data make one request.
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            {/* Partner `?pt=` capture at the root, BEFORE any route guard can
                redirect and lose the query string (2026-09-25). */}
            <PartnerTokenCapture />
            <Outlet />
            {/* Consent UI mounted at the ROOT (above the router) so it also
                covers the public /pay checkout — the primary ad-conversion page.
                Cookie policy lives on the marketing site. See
                CONVERSION_TRACKING_ARCHITECTURE_2026-07-08.md (AD-7). */}
            <CookieBanner policyHref={`${siteConfig.url}/cookies`} />
            <GoogleAdsConsentBridge />
          </AuthProvider>
        </QueryClientProvider>
      }
      HydrateFallback={PortalHydrateFallback}
    >
      <Route path="*" element={<AppRoutes />} />
    </Route>
  )
);

export function App() {
  return <RouterProvider router={router} />;
}
